/**
 * Coach/partner sharing schemas (T16.5 / ISSUE-081).
 *
 * Read-only week-summary view shared with a coach or partner via a secure
 * invite token. Scope: weekly summary, weight trend, adherence calendar,
 * target history. Chat content and raw logs are NEVER shared (privacy by
 * default; excluded unless separately consented, which v1 does not support).
 *
 * B2B forward-compat: the schema is intentionally minimal; a future gym/coach
 * org tier will add org_id management without migrating the coach_link table.
 */
import { z } from 'zod';
export const coachLinkScopeSchema = z.enum(['week_summary']);
export const coachLinkStatusSchema = z.enum(['pending', 'active', 'revoked']);
/** Full link record returned to the owner. */
export const coachLinkSchema = z.object({
    id: z.string().uuid(),
    ownerUserId: z.string(),
    granteeEmail: z.string().email(),
    /** Null until the grantee accepts. */
    granteeUserId: z.string().nullable(),
    scope: coachLinkScopeSchema,
    status: coachLinkStatusSchema,
    tokenExpiresAt: z.string().datetime().nullable(),
    revokedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** POST /me/sharing/coach-links body. */
export const createCoachLinkSchema = z.object({
    granteeEmail: z.string().email({ message: 'A valid email is required.' }),
    scope: coachLinkScopeSchema.default('week_summary'),
});
/** POST /me/sharing/coach-links/accept body. */
export const acceptCoachLinkSchema = z.object({
    token: z.string().min(1),
});
// ── Shared view ───────────────────────────────────────────────────────────────
/** One day of adherence: was there at least one logged meal entry? */
export const adherenceDaySchema = z.object({
    date: z.string().date(),
    hasLog: z.boolean(),
});
/** Weight trend data-point for the shared view. */
export const sharedWeightPointSchema = z.object({
    date: z.string().date(),
    valueKg: z.number(),
    ewmaKg: z.number().nullable(),
});
/** Minimal target snapshot for the shared view (no personal notes). */
export const sharedTargetSnapshotSchema = z.object({
    effectiveFrom: z.string().date(),
    kcal: z.number().int(),
    proteinG: z.number().int(),
    carbsG: z.number().int(),
    fatG: z.number().int(),
    fiberG: z.number().int(),
    source: z.string(),
});
export const sharedWeeklySummarySchema = z.object({
    periodStart: z.string().date(),
    payload: z.record(z.string(), z.unknown()),
});
/**
 * Read-only week view returned to a grantee for GET
 * /me/sharing/coach-views/:ownerUserId/week.
 *
 * PRIVACY: includes only aggregates and trends.
 * NEVER includes chat content, raw log text, or individual food entries.
 */
export const sharedWeekViewSchema = z.object({
    ownerUserId: z.string(),
    ownerName: z.string().nullable(),
    /** Latest weekly summary payload; aggregate only, no raw log text or chat content. */
    weeklySummary: sharedWeeklySummarySchema.nullable(),
    /** Latest ~12 weeks of weight trend (body_metric type=weight). */
    weightTrend: z.array(sharedWeightPointSchema),
    /** Day-by-day presence of meal logging over the last 90 days. */
    adherenceCalendar: z.array(adherenceDaySchema),
    /** The 5 most recent target rows (target history). */
    targetHistory: z.array(sharedTargetSnapshotSchema),
});
/** Item returned in GET /me/sharing/coach-views — list of owners sharing with me. */
export const coachViewSummarySchema = z.object({
    linkId: z.string().uuid(),
    ownerUserId: z.string(),
    ownerName: z.string().nullable(),
    scope: coachLinkScopeSchema,
    linkedAt: z.string().datetime(),
});
//# sourceMappingURL=sharing.js.map